import React, { useState, useEffect } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import { useTenant } from '../contexts/TenantContext'
import { getEstadoPagamento } from '../lib/supabase'
import { NotaTeste } from '../components/AvisoTeste'

function DetalheItem({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
      <span style={{ color: 'var(--cor-texto-suave)', minWidth: '100px', flexShrink: 0 }}>
        {label}:
      </span>
      <strong>{value}</strong>
    </div>
  )
}

export default function Confirmacao() {
  const { state } = useLocation()
  const { tenant } = useTenant()
  const [searchParams] = useSearchParams()

  const veioDoStripe = searchParams.get('pago') === '1'
  const sessionId = searchParams.get('session_id')

  // Detalhes da marcação: vêm do state da navegação (fluxo sem pagamento) ou do
  // sessionStorage guardado antes do checkout (regresso do Stripe).
  const [detalhes] = useState(() => {
    if (state?.selecao) return { selecao: state.selecao, profissionalNome: state.profissionalNome, sinal: null }
    if (veioDoStripe) {
      try {
        const g = JSON.parse(sessionStorage.getItem('confirmacaoPendente') || 'null')
        if (g) return { selecao: g.selecao, profissionalNome: g.profissionalNome, sinal: g.sinal }
      } catch { /* ignora */ }
    }
    return null
  })

  // Estado do pagamento: 'na' (sem pagamento), 'processando', 'confirmado',
  // 'falhado' (slot ficou indisponível → sinal devolvido), 'demora' (webhook lento).
  const [estado, setEstado] = useState(veioDoStripe ? 'processando' : 'na')

  useEffect(() => {
    if (!veioDoStripe) return
    if (!sessionId || !tenant) { setEstado('demora'); return }

    let ativo = true
    let tentativas = 0
    let timer

    async function verificar() {
      if (!ativo) return
      try {
        const e = await getEstadoPagamento(tenant.id, sessionId)
        if (!ativo) return
        if (e === 'confirmado' || e === 'falhado') {
          setEstado(e)
          sessionStorage.removeItem('confirmacaoPendente')
          return
        }
      } catch { /* tenta de novo */ }
      tentativas++
      if (tentativas >= 8) { setEstado('demora'); return }
      timer = setTimeout(verificar, 2000)
    }
    verificar()
    return () => { ativo = false; clearTimeout(timer) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veioDoStripe, sessionId, tenant])

  const wrap = { maxWidth: '480px', margin: '0 auto', padding: '64px 24px', textAlign: 'center' }

  // A processar o pagamento — à espera do webhook do Stripe.
  if (estado === 'processando') {
    return (
      <main style={wrap}>
        <div style={{ fontSize: '52px', marginBottom: '16px', lineHeight: 1 }}>⏳</div>
        <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>A confirmar o pagamento…</h2>
        <p style={{ color: 'var(--cor-texto-suave)', lineHeight: 1.6 }}>
          Aguarde um instante, estamos a finalizar a sua marcação.
        </p>
      </main>
    )
  }

  // O slot ficou indisponível durante o pagamento — sinal devolvido.
  if (estado === 'falhado') {
    return (
      <main style={wrap}>
        <div style={{ fontSize: '56px', marginBottom: '16px', lineHeight: 1 }}>↩️</div>
        <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>Horário indisponível</h2>
        <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', lineHeight: 1.6 }}>
          Esse horário ficou ocupado enquanto o pagamento era processado, por isso
          <strong> o sinal foi devolvido</strong>. Por favor escolha outro horário.
        </p>
        <Link to="/agendar" className="btn-primario" style={{ textDecoration: 'none', display: 'inline-block' }}>
          Escolher outro horário
        </Link>
      </main>
    )
  }

  // Sem detalhes para mostrar (acesso direto à página).
  if (!detalhes) {
    return (
      <main style={{ maxWidth: '480px', margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '12px' }}>Página não encontrada</h2>
        <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '24px' }}>
          Aceda à página inicial para fazer uma nova marcação.
        </p>
        <Link to="/" className="btn-primario" style={{ textDecoration: 'none', display: 'inline-block' }}>
          Ir para o Início
        </Link>
      </main>
    )
  }

  const { selecao, profissionalNome, sinal } = detalhes
  const sinalPago = estado === 'confirmado' ? sinal : null
  // 'demora': webhook lento — pagamento recebido mas ainda a finalizar.
  const aindaAfinalizar = estado === 'demora'

  return (
    <main style={wrap}>
      <div style={{ fontSize: '60px', marginBottom: '16px', lineHeight: 1 }}>{aindaAfinalizar ? '💳' : '✅'}</div>
      <h2 style={{ fontSize: '26px', marginBottom: '10px' }}>
        {aindaAfinalizar ? 'Pagamento recebido!' : 'Marcação Confirmada!'}
      </h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: (sinalPago || aindaAfinalizar) ? '20px' : '36px', lineHeight: 1.6 }}>
        {aindaAfinalizar
          ? <>Estamos a finalizar a sua marcação.<br />Receberá a confirmação por email.</>
          : <>A sua marcação foi registada com sucesso.<br />Até breve!</>}
      </p>

      <NotaTeste style={{ marginBottom: '28px' }}>
        esta marcação é de teste e não é real. Ninguém o espera no espaço.
      </NotaTeste>

      {sinalPago > 0 && (
        <p style={{
          marginBottom: '32px', lineHeight: 1.6, fontSize: '14px',
          color: 'var(--cor-texto)', background: 'var(--cor-creme)',
          border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
          padding: '12px 16px',
        }}>
          Sinal de <strong>{Number(sinalPago).toFixed(2)} €</strong> pago com sucesso.<br />
          O restante valor será liquidado no próprio dia.
        </p>
      )}

      <div style={{
        background: 'var(--cor-branco)',
        border: '1px solid var(--cor-borda)',
        borderRadius: 'var(--raio-lg)',
        padding: '24px',
        textAlign: 'left',
        boxShadow: 'var(--sombra)',
        marginBottom: '32px',
      }}>
        <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '16px', fontSize: '15px' }}>
          Detalhes da Marcação
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <DetalheItem label="Serviço" value={selecao.servico?.nome} />
          <DetalheItem
            label="Data"
            value={
              selecao.data
                ? format(new Date(selecao.data + 'T12:00:00'), "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt })
                : '—'
            }
          />
          <DetalheItem
            label="Hora"
            value={selecao.slot ? format(new Date(selecao.slot), 'HH:mm') : '—'}
          />
          <DetalheItem label="Profissional" value={profissionalNome} />
          <div style={{ height: '1px', background: 'var(--cor-borda)', margin: '4px 0' }} />
          <DetalheItem label="Nome" value={selecao.cliente?.nome} />
          <DetalheItem label="Telefone" value={selecao.cliente?.telefone} />
          {selecao.cliente?.email && (
            <DetalheItem label="Email" value={selecao.cliente.email} />
          )}
          {selecao.cliente?.notas && (
            <DetalheItem label="Notas" value={selecao.cliente.notas} />
          )}
        </div>
      </div>

      <div className="acao-form" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link
          to="/agendar"
          className="btn-secundario"
          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          Nova Marcação
        </Link>
        <Link
          to="/"
          className="btn-primario"
          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          Ir para o Início
        </Link>
      </div>
    </main>
  )
}
